import { Map } from 'immutable';
import createCachedSelector from 're-reselect';
import { createSelector } from '@reduxjs/toolkit';

import { ReduxState } from 'app/redux/types';

import { isFinishedMatch } from 'app/lib/betting/accessMatchDetails';
import { LineEntryResult, MatchEntryResult } from 'app/lib/betting/betting-entities';
import { getOrderedStakeTypeGroupIdsConf } from 'app/lib/matchGridStakes';

import { StakeTypeGroup } from '../../../Betting';
import { matches, sports } from './entities';

// ---

type MatchId = number;
type MainLineStakesGrid = Map<MatchId, MatchEntryResult>;

const selectMainLineStakesGrid = (state: ReduxState): MainLineStakesGrid => {
  return state.matchOfTheDay.get('grid').get('mainLineStakesGrid');
};

const mainStakesSelector = (
  state: ReduxState,
  matchId: number,
  lineId: number,
): LineEntryResult | null => {
  return selectMainLineStakesGrid(state)
    ?.get(matchId)
    ?.get(lineId) || null;
};

export const selectNotFinishedMatchIds = createSelector(
  matches,
  (matches) => {
    return matches.reduce((acc: number[], match) => {
      if (match.startDate === undefined || isFinishedMatch(match)) {
        return acc;
      }

      acc.push(match.id);

      return acc;
    }, []);
  },
);

export const sportIdByMatchIdSelector = createCachedSelector(
  sports,
  (_state: ReduxState, matchId: number) => matchId,
  (sports, matchId) => {
    const sport = sports.find((sport) => sport.matchIds.has(matchId));
    return sport?.id;
  })((_state, matchId) => matchId);

export type SortedStakeByStakeTypeGroup = {
  stakeTypeGroupId: keyof typeof StakeTypeGroup;
  stakeId: number;
};

const MAX_STAKES = 3;

export const sortedStakeByStakeTypeGroupIdsSelector = createCachedSelector(
  (state: ReduxState, matchId: number) => sportIdByMatchIdSelector(state, matchId),
  (state: ReduxState, matchId: number, lineId: number) => (
    mainStakesSelector(state, matchId, lineId)
  ),
  (sportId, mainStakes) => {
    const orderedStakeTypeGroupIds = getOrderedStakeTypeGroupIdsConf(sportId);

    if (!mainStakes) {
      return [];
    }

    const sorted: SortedStakeByStakeTypeGroup[] = [];
    orderedStakeTypeGroupIds.forEach((stakeTypeGroupId: SortedStakeByStakeTypeGroup['stakeTypeGroupId']) => {
      const stakeId = mainStakes.get(stakeTypeGroupId);
      stakeId && sorted.push({ stakeTypeGroupId, stakeId });
    });

    return sorted.slice(0, MAX_STAKES);
  })((_state, matchId, lineId) => `${matchId}.${lineId}`);
